import {useMemo} from 'react';

import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {makeReactContext} from 'lib/makeReactContext';
import {
  PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode as PMEI,
  PMEIsQuery_procedureMethodExecutionInstances_ProcedureMethodExecutionInstanceNode_methodTask_ProcessTaskNode as MethodTask,
} from 'procedures/worksheets/__generated/PMEIsQuery';

export interface ProcedureWorksheetContextValue {
  PMEIs: Array<PMEIOrInaccessible>;
  refetchPMEIs: () => Promise<void>;
  isProcedureFeatureEnabled: boolean;
}

export function getProcedureWorksheetContextFallback(): ProcedureWorksheetContextValue {
  return {
    PMEIs: [],
    refetchPMEIs: () => Promise.resolve(),
    isProcedureFeatureEnabled: false,
  };
}

export const ProcedureWorksheetContext = makeReactContext<ProcedureWorksheetContextValue>(
  getProcedureWorksheetContextFallback,
  'ProcedureWorksheetContext'
);

export const useProcedureWorksheetContext = (): ProcedureWorksheetContextValue => {
  const context = ProcedureWorksheetContext.useContext();
  return context;
};

/**
 * Returns only the PMEIs that have linked method tasks. Method tasks are linked asynchronously
 * after PMEI creation, so this list may be empty even when PMEIs exist for the worksheet.
 *
 * Use this when you need method task data (status, transitions, timestamps). Do NOT use this
 * to check PMEI-level properties like procedureRun.defaultDataEntryMode — use
 * useProcedureWorksheetContext().PMEIs instead, which includes all PMEIs regardless of
 * method task linkage status.
 */
export const usePMEIsAndMethodTasks = (): Array<{task: MethodTask; pmei: PMEI}> => {
  const {PMEIs} = useProcedureWorksheetContext();
  return useMemo(
    () =>
      PMEIs.filter(
        (pmei) => !isInaccessible(pmei) && pmei.methodTask && !isInaccessible(pmei.methodTask)
      ).map((pmei) => ({task: (pmei as PMEI).methodTask as MethodTask, pmei: pmei as PMEI})),
    [PMEIs]
  );
};
